import React, { useState, useEffect, useRef } from 'react';
import Button from '@material-ui/core/Button';
import { useTranslation } from 'react-i18next';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { isMobile } from '@/biz/tool';
import MobileSteps from './MobileSteps';

interface Props {
  step: number;
  setStep: Function;
  maxStep: number;
  setMaxStep: Function;
  isAffiliate: boolean;
}

const Payment: React.FC<Props> = ({
  step,
  setStep,
  maxStep,
  setMaxStep,
  isAffiliate
}) => {
  const [time, setTime] = useState(10);
  const timeRef = useRef(time);
  const { setEducationModal } = useWebModalStore();
  const { t } = useTranslation();

  // 同步 time 到 ref
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    const educationModalEnd = localStorage.getItem('educationModalEnd');

    // 如果已经完成或已结束，直接设置为0
    if (maxStep > step || !!educationModalEnd) {
      setTime(0);
      return;
    }

    // 如果时间大于0，开始倒计时
    if (timeRef.current > 0) {
      const timer = setTimeout(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [time, maxStep, step]); // 添加所有依赖项

  const handleSubmit = () => {
    localStorage.removeItem('educationModalStart');
    localStorage.setItem('educationModalEnd', '1');
    setEducationModal(false, {});
    setStep(0);
  };

  return (
    <div
      className={`w-full bg-white pb-8 flex flex-col justify-between ${isMobile() ? 'px-0' : 'px-8'}`}
    >
      <div className="w-full py-5 border-b border-[#e8e8ec] inline-flex flex-col justify-center items-start gap-2">
        <div className="justify-start text-[#1c2024] text-2xl font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
          {t('modal.education.payment.title', { defaultValue: 'Payment' })}
        </div>
        {isMobile() && (
          <MobileSteps step={step} maxStep={maxStep} setStep={setStep} />
        )}
        <div className="w-full h-[27px] justify-center text-[#60646c] text-sm font-normal font-['Inter'] leading-tight">
          {t('modal.education.payment.subtitle', {
            defaultValue: 'Discover how your payment is secured on the platform'
          })}
        </div>
      </div>
      <div className={`${isMobile() ? '' : 'h-[500px] pb-8 overflow-y-auto'}`}>
        <div
          className={`mt-[52px] flex items-start gap-8 w-full ${isMobile() ? 'flex-col' : ''}`}
        >
          <div className={`${isMobile() ? 'w-full' : 'w-[50%-16px]'}`}>
            <img
              src={
                isAffiliate
                  ? '/education-payment1-affiliate.png'
                  : '/education-payment1.png'
              }
              className="w-full"
              alt=""
            />
            <div className="self-stretch text-center justify-start text-[#1c2024] text-sm font-medium font-['Plus_Jakarta_Sans'] leading-normal">
              {isAffiliate
                ? t('modal.education.payment.affiliate_step1', {
                    defaultValue:
                      '1. You can view the sales amount and  commission for each affiliate deal in the Transaction History section of your wallet.'
                  })
                : t('modal.education.payment.step1', {
                    defaultValue:
                      '1. 100% of the payments will be credited to your wallet once the collaboration is confirmed by brand*'
                  })}
            </div>
          </div>
          <div className={`${isMobile() ? 'w-full' : 'w-[50%-16px]'}`}>
            <img
              src={
                isAffiliate
                  ? '/education-payment2-affiliate.png'
                  : '/education-payment2.png'
              }
              className="w-full"
              alt=""
            />
            <div className="self-stretch text-center justify-start">
              <span className="text-[#1c2024] text-sm font-medium font-['Plus_Jakarta_Sans'] leading-normal">
                {isAffiliate
                  ? t('modal.education.payment.affiliate_step2_part1', {
                      defaultValue:
                        '2. You can withdraw the funds from your wallet '
                    })
                  : t('modal.education.payment.step2_part1', {
                      defaultValue: '2. Withdraw the balance immediately'
                    })}
                <br />
              </span>
              <span className="text-[#1c2024] text-sm font-medium font-['Plus_Jakarta_Sans'] leading-normal">
                {isAffiliate
                  ? t('modal.education.payment.affiliate_step2_part2', {
                      defaultValue: '30 days after a sale is made.'
                    })
                  : t('modal.education.payment.step2_part2', {
                      defaultValue: 'once the collaboration is completed'
                    })}
              </span>
            </div>
          </div>
        </div>
        <div className="w-full mt-8 justify-start text-[#1c2024] text-base font-medium font-['Plus_Jakarta_Sans'] leading-7">
          {t('modal.education.payment.notes', {
            defaultValue:
              '*Notes:  Brands may take up to 5 business days or longer to review your application. The average brand approval rate is approximately 50%. If you do not receive a response from the brand within 5 business days, you have the option to withdraw from the deal.'
          })}
        </div>
      </div>
      <div className="flex items-center justify-end gap-4 mt-8">
        <Button
          className="mui-btn-gray !w-[160px] !h-[40px]"
          onClick={() => {
            setStep(step - 1);
            setMaxStep(Math.max(maxStep, step + 1));
          }}
        >
          {t('modal.education.payment.back_btn', { defaultValue: 'Back' })}
        </Button>
        <Button
          className="mui-btn-primary !w-[160px] !h-[40px]"
          onClick={handleSubmit}
        >
          {t('modal.education.payment.complete_btn', {
            defaultValue: 'Complete'
          })}{' '}
        </Button>
      </div>
    </div>
  );
};

export default Payment;
